import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const VIEW_WIDTH = 700;
const VIEW_HEIGHT = 350;
const MARGIN = { top: 50, right: 180, bottom: 20, left: 140 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function PriceFunnelChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const tiers = useMemo(() => {
    if (!data || !data.tiers || data.tiers.length === 0) return [];
    return data.tiers;
  }, [data]);

  const ratios = useMemo(() => {
    if (!data || !data.ratios) return [];
    return data.ratios;
  }, [data]);

  useEffect(() => {
    if (!tiers || tiers.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg.attr('viewBox', `0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const defs = svg.append('defs');

    const innerW = VIEW_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = VIEW_HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scale: width proportional to avgPrice
    const maxAvg = d3.max(tiers, (d) => d.avgPrice) || 1;
    const maxRange = d3.max(tiers, (d) => d.maxPrice) || maxAvg;
    const xScale = d3.scaleLinear()
      .domain([0, maxRange])
      .range([0, innerW]);

    // Tier height and spacing
    const tierCount = tiers.length;
    const ratioCount = ratios.length;
    const ratioGap = 22;
    const totalRatioSpace = ratioCount * ratioGap;
    const barHeight = Math.min(36, (innerH - totalRatioSpace) / tierCount);
    const totalHeight = tierCount * barHeight + totalRatioSpace;
    const yOffset = (innerH - totalHeight) / 2;

    // Create gradient for each tier
    tiers.forEach((tier, i) => {
      const gradId = `funnel-grad-${i}`;
      const grad = defs.append('linearGradient')
        .attr('id', gradId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%');

      const baseColor = d3.color(tier.color) || d3.color('#6b7280');
      grad.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', baseColor.brighter(0.4).toString());
      grad.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', baseColor.darker(0.3).toString());
    });

    // Draw each tier
    let currentY = yOffset;

    tiers.forEach((tier, i) => {
      const barW = xScale(tier.avgPrice);
      const barX = (innerW - barW) / 2;
      const barY = currentY;

      // Trapezoid shape: slightly wider at bottom for funnel effect
      // The cheapest tier (first) is narrowest, most expensive (last) is widest
      const taperOffset = 4;
      const topW = barW - taperOffset;
      const bottomW = barW + taperOffset;
      const topX = (innerW - topW) / 2;
      const bottomX = (innerW - bottomW) / 2;

      const trapezoid = [
        [topX, barY],
        [topX + topW, barY],
        [bottomX + bottomW, barY + barHeight],
        [bottomX, barY + barHeight],
      ];

      const tierGroup = g.append('g')
        .attr('class', 'tier-group')
        .style('cursor', 'pointer');

      // Main trapezoid bar with gradient fill
      tierGroup.append('polygon')
        .attr('points', trapezoid.map((p) => p.join(',')).join(' '))
        .attr('fill', `url(#funnel-grad-${i})`)
        .attr('stroke', tier.color)
        .attr('stroke-width', 0.5)
        .attr('opacity', 0.9);

      // Min-max whisker lines
      const whiskerY = barY + barHeight / 2;
      const minX = (innerW - xScale(tier.minPrice)) / 2;
      const maxX = (innerW + xScale(tier.maxPrice)) / 2;

      // Left whisker (min)
      tierGroup.append('line')
        .attr('x1', minX)
        .attr('x2', Math.min(topX, bottomX))
        .attr('y1', whiskerY)
        .attr('y2', whiskerY)
        .attr('stroke', tier.color)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '3,2')
        .attr('opacity', 0.6);

      // Left whisker cap
      tierGroup.append('line')
        .attr('x1', minX)
        .attr('x2', minX)
        .attr('y1', whiskerY - 4)
        .attr('y2', whiskerY + 4)
        .attr('stroke', tier.color)
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.6);

      // Right whisker (max)
      tierGroup.append('line')
        .attr('x1', Math.max(topX + topW, bottomX + bottomW))
        .attr('x2', maxX)
        .attr('y1', whiskerY)
        .attr('y2', whiskerY)
        .attr('stroke', tier.color)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '3,2')
        .attr('opacity', 0.6);

      // Right whisker cap
      tierGroup.append('line')
        .attr('x1', maxX)
        .attr('x2', maxX)
        .attr('y1', whiskerY - 4)
        .attr('y2', whiskerY + 4)
        .attr('stroke', tier.color)
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.6);

      // Left label (tier name)
      tierGroup.append('text')
        .attr('x', -10)
        .attr('y', barY + barHeight / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(tier.label);

      // Right label (avg price)
      tierGroup.append('text')
        .attr('x', innerW + 10)
        .attr('y', barY + barHeight / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'start')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', tier.color)
        .text(`$${tier.avgPrice.toFixed(2)}`);

      // Right-side spread bar (further right)
      const spreadBarX = innerW + 70;
      const spreadBarMaxW = 80;
      const maxSpread = d3.max(tiers, (d) => d.spread) || 1;
      const spreadW = (tier.spread / maxSpread) * spreadBarMaxW;

      tierGroup.append('rect')
        .attr('x', spreadBarX)
        .attr('y', barY + barHeight / 2 - 4)
        .attr('width', spreadBarMaxW)
        .attr('height', 8)
        .attr('fill', '#f3f4f6')
        .attr('rx', 4);

      tierGroup.append('rect')
        .attr('x', spreadBarX)
        .attr('y', barY + barHeight / 2 - 4)
        .attr('width', spreadW)
        .attr('height', 8)
        .attr('fill', tier.color)
        .attr('opacity', 0.5)
        .attr('rx', 4);

      tierGroup.append('text')
        .attr('x', spreadBarX + spreadBarMaxW + 6)
        .attr('y', barY + barHeight / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'start')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280')
        .text(`$${tier.spread.toFixed(0)}`);

      // Invisible hover target
      tierGroup.append('rect')
        .attr('x', -MARGIN.left + 10)
        .attr('y', barY)
        .attr('width', VIEW_WIDTH - 20)
        .attr('height', barHeight)
        .attr('fill', 'transparent')
        .on('mouseenter', (event) => {
          tierGroup.select('polygon')
            .transition()
            .duration(150)
            .attr('opacity', 1);
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 14,
            tier,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev
              ? {
                  ...prev,
                  x: event.clientX + 14,
                  y: event.clientY - 14,
                }
              : prev
          );
        })
        .on('mouseleave', () => {
          tierGroup.select('polygon')
            .transition()
            .duration(150)
            .attr('opacity', 0.9);
          setTooltip(null);
        });

      currentY += barHeight;

      // Ratio annotation between tiers
      if (i < ratios.length) {
        const ratio = ratios[i];
        const ratioY = currentY + ratioGap / 2;

        // Arrow line
        g.append('line')
          .attr('x1', innerW / 2 - 30)
          .attr('x2', innerW / 2 + 30)
          .attr('y1', ratioY)
          .attr('y2', ratioY)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1);

        // Arrow head
        g.append('path')
          .attr('d', `M${innerW / 2 + 25},${ratioY - 3} L${innerW / 2 + 30},${ratioY} L${innerW / 2 + 25},${ratioY + 3}`)
          .attr('fill', 'none')
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1);

        // Ratio label
        g.append('text')
          .attr('x', innerW / 2)
          .attr('y', ratioY - 5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', '#6b7280')
          .text(ratio.label || `${ratio.ratio.toFixed(1)}x`);

        currentY += ratioGap;
      }
    });

    // Spread column header
    const spreadHeaderX = innerW + 70;
    g.append('text')
      .attr('x', spreadHeaderX + 40)
      .attr('y', yOffset - 12)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 600)
      .attr('fill', '#6b7280')
      .attr('text-transform', 'uppercase')
      .text('Spread');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [tiers, ratios]);

  if (!data || !data.tiers || data.tiers.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Service Tier Price Funnel</div>
          <div style={styles.subtitle}>Average pricing across service tiers with relative multipliers</div>
        </div>
        <div style={styles.emptyState}>No data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Service Tier Price Funnel</div>
        <div style={styles.subtitle}>Average pricing across service tiers with relative multipliers</div>
      </div>
      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '6px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: tooltip.tier.color,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            {tooltip.tier.label}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Avg Price</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>${tooltip.tier.avgPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Median</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>${tooltip.tier.medianPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Min</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>${tooltip.tier.minPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Max</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>${tooltip.tier.maxPrice.toFixed(2)}</span>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Spread</span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>${tooltip.tier.spread.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Addresses</span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>{tooltip.tier.addressCount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
